import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Coins, Pencil, Check, X, Loader2 } from "lucide-react";

const ROWS = [
  ["flexible", "Flexible"],
  ["360", "360 day"],
  ["540", "540 day"],
  ["720", "720 day"],
];

const BADGE = {
  carypact: { label: "CaryPact", color: "#0E7490" },
  admin: { label: "typed in", color: "#7C3AED" },
  dapp: { label: "dApp", color: "#0E7490" },
};

const when = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  return days <= 0 ? "today" : days === 1 ? "yesterday" : `${days}d ago`;
};

/**
 * The BOT pledge rate table. CaryPact publishes the CA plan table but nothing we
 * can read for BOT, so each row says where its number came from: read from
 * CaryPact, typed in by an admin, or measured off the team's own pledges.
 */
export default function LiveBotRates({ data, onSaved }) {
  const { user } = useAuth();
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setEdit(null); }, [data?.updated_at]);

  if (!data) return null;
  const rates = data.rates || {};
  const sources = data.sources || {};
  const observed = data.observed || {};
  const canEdit = user?.role === "admin" && !user?.is_demo;

  const save = async () => {
    const body = {};
    const clear = [];
    for (const [k] of ROWS) {
      const v = String(edit[k] ?? "").trim();
      const current = rates[k] == null ? "" : String(rates[k]);
      // An untouched row is left exactly as it is — otherwise saving one number
      // would freeze every measured row as a hand-typed one.
      if (v === current) continue;
      // An emptied row goes back to the dApp's published rate.
      if (v === "") { if (sources[k] === "admin" || sources[k] === "carypact") clear.push(k); continue; }
      const n = Number(v);
      if (Number.isNaN(n) || n < 0 || n > 5) { toast.error(`${k}: enter a %/day between 0 and 5`); return; }
      body[k] = n;
    }
    if (clear.length) body.clear = clear;
    if (!Object.keys(body).length) { setEdit(null); return; }
    setSaving(true);
    try {
      const { data: d } = await api.put("/bot-staking-rates", body);
      onSaved?.(d);
      setEdit(null);
      toast.success("BOT rates updated — the calculator uses them right away");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save the BOT rates");
    } finally { setSaving(false); }
  };

  return (
    <div className="glass-strong p-4" data-testid="live-bot-rates">
      <div className="flex items-center gap-2 mb-2">
        <Coins size={14} style={{ color: "#0E7490" }} />
        <h3 className="font-display text-sm font-semibold tracking-tight flex-1">BOT pledge rates</h3>
        {canEdit && !edit && (
          <button type="button" onClick={() => setEdit({ ...rates })} title="Type the rates from the CaryPact dApp"
            className="text-ink-3 hover:text-[#0E7490] transition-colors" data-testid="bot-rates-edit-btn">
            <Pencil size={12} />
          </button>
        )}
        {edit && (
          <div className="flex items-center gap-1">
            <button type="button" onClick={save} disabled={saving} className="text-[#00863A] disabled:opacity-40" data-testid="bot-rates-save-btn" title="Save">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={14} />}
            </button>
            <button type="button" onClick={() => setEdit(null)} className="text-ink-3 hover:text-rose-600" data-testid="bot-rates-cancel-btn" title="Cancel">
              <X size={13} />
            </button>
          </div>
        )}
      </div>

      <p className="text-[10px] text-ink-3 leading-snug mb-2" data-testid="bot-rates-source">
        {data.source === "carypact"
          ? `Read from CaryPact's own BOT plan table${when(data.updated_at) ? ` · ${when(data.updated_at)}` : ""}.`
          : data.source === "admin"
            ? `Typed in${when(data.updated_at) ? ` · ${when(data.updated_at)}` : ""}${data.updated_by ? ` by ${data.updated_by}` : ""}. Rows left blank fall back to the dApp's published rate.`
            : "The daily rates (DPR) the dApp's BOT Pledge screen shows for each plan. Hover a row to see what your own orders have actually paid."}
      </p>

      <ul className="space-y-1 text-xs font-mono tabular-nums" data-testid="bot-rates-list">
        {ROWS.map(([k, label]) => {
          const src = BADGE[sources[k]];
          const obs = observed[k];
          return (
            <li key={k} className="flex items-center justify-between gap-2" data-testid={`bot-rates-row-${k}`}>
              <span className="text-ink-2 flex items-center gap-1.5">
                {label}
                {src && !edit && (
                  <span className="text-[9px] font-bold uppercase tracking-wide" style={{ color: src.color }}
                    title={obs ? `Your orders: ${obs.wallets} wallet${obs.wallets === 1 ? "" : "s"} paid ${obs.min}–${obs.max}%/day (${obs.from} → ${obs.to})` : undefined}>
                    {src.label}
                  </span>
                )}
              </span>
              {edit ? (
                <span className="flex items-center gap-1">
                  <input
                    value={edit[k] ?? ""}
                    onChange={(e) => setEdit({ ...edit, [k]: e.target.value })}
                    inputMode="decimal"
                    className="w-16 text-right px-1.5 py-0.5 rounded border bg-white text-xs"
                    style={{ borderColor: "rgba(15,18,38,0.15)" }}
                    data-testid={`bot-rates-input-${k}`}
                  />
                  <span className="text-ink-3">%/day</span>
                </span>
              ) : (
                <span className="font-semibold text-ink">
                  {rates[k] != null ? `${rates[k]}%/day` : <span className="text-ink-3" title="Nobody has given us this one yet">—</span>}
                </span>
              )}
            </li>
          );
        })}
      </ul>

      <p className="text-[10px] text-ink-3 leading-snug mt-2.5 pt-2.5 border-t" style={{ borderColor: "rgba(15,18,38,0.07)" }}>
        A fixed pledge also hands back <b>1/term of your BOT every day</b> while the rewards keep
        running on the whole pledge. BOT ${Number(data.bot_price || 0).toFixed(2)}.
      </p>
    </div>
  );
}
